import { API_SERVICEPROVIDERS_BACKEND, API_USERS_BACKEND } from "./api";
import type {
  ServiceProviderRecord,
  StudentCaseloadRecord,
  CampusRef,
  DistrictRef,
  MedicaidCredential,
  ProviderTypeRecord,
} from "@xng/xng-backend";
import type {
  ServiceProviderResponse as OldServiceProviderResponse,
  StudentRef as OldStudentRef,
  SchoolCampusAssignment as OldSchoolCampusAssignment,
  ServiceProviderType as OldServiceProviderType,
} from "../profile-sdk";

/**
 * Assembles an old-shaped ServiceProviderResponse from the decomposed backend APIs.
 * Calls: GetServiceProvider + GetStudentCaseload + GetDistricts + GetCampuses + GetCredentials.
 * Pure async function — no hooks, no dispatch.
 */
export async function fetchServiceProviderResponse(
  serviceProviderId: string,
): Promise<OldServiceProviderResponse> {
  const [sp, studentCaseload, districts, campuses, credentials] = await Promise.all([
    API_SERVICEPROVIDERS_BACKEND
      .getServiceProvider({ serviceProviderId }),
    API_SERVICEPROVIDERS_BACKEND
      .getStudentCaseload({ serviceProviderId })
      .catch(() => [] as StudentCaseloadRecord[]),
    API_SERVICEPROVIDERS_BACKEND
      .getServiceProviderDistricts({ serviceProviderId })
      .catch(() => [] as DistrictRef[]),
    API_SERVICEPROVIDERS_BACKEND
      .getCampuses({ serviceProviderId })
      .catch(() => [] as CampusRef[]),
    API_SERVICEPROVIDERS_BACKEND
      .getCredentials({ serviceProviderId })
      .catch(() => [] as MedicaidCredential[]),
  ]);

  // ProviderTypes lives on the User, not the SP — need the userId from the SP record
  let providerTypes: ProviderTypeRecord[] = [];
  if (sp.user?.id) {
    providerTypes = await API_USERS_BACKEND
      .getProviderTypes({ id: sp.user.id })
      .catch(() => [] as ProviderTypeRecord[]);
  }

  return buildServiceProviderResponse(sp, studentCaseload, districts, campuses, credentials, providerTypes);
}

// ── internal ────────────────────────────────────────────────────────────

function buildServiceProviderResponse(
  sp: ServiceProviderRecord,
  studentCaseload: StudentCaseloadRecord[],
  districts: DistrictRef[],
  campuses: CampusRef[],
  credentials: MedicaidCredential[],
  providerTypes: ProviderTypeRecord[],
): OldServiceProviderResponse {
  const shimmedStudents = studentCaseload
    .filter((r) => r.student)
    .map((r) => ({
      id: r.student!.id,
      firstName: r.student!.firstName,
      middleName: r.student!.middleName,
      lastName: r.student!.lastName,
      dateOfBirth: r.student!.dateOfBirth instanceof Date
        ? r.student!.dateOfBirth.toISOString()
        : r.student!.dateOfBirth,
      medicaidId: r.student!.medicaidId != null ? String(r.student!.medicaidId) : undefined,
    })) as OldStudentRef[];

  const shimmedCampuses: OldSchoolCampusAssignment[] = campuses.map((c) => ({
    id: c.id,
    name: c.name,
  }));

  const shimmedProviderTypes: OldServiceProviderType[] = providerTypes.map((t) => ({
    id: t.id != null ? (t.id as any) : undefined,
    name: t.name,
  }));

  return {
    id: sp.id,
    firstName: sp.user?.firstName,
    lastName: sp.user?.lastName,
    email: sp.user?.emailAddress,
    rtmsEmail: sp.rtmsEmail ?? undefined,
    employeeId: sp.employeeId ?? undefined,
    documentationType: sp.documentationType as any,
    classType: sp.classType as any,
    phoneNumber: sp.user?.phoneNumber ?? undefined,
    jobTitle: sp.jobTitle ?? undefined,
    employeeType: sp.employeeType as any,
    isOnParticipationList: sp.isOnParticipationList,
    participationListStatus: sp.participationListStatus as any,
    npi: sp.npi ?? undefined,
    stateMedicaidNumber: sp.stateMedicaidNumber ?? undefined,
    blockBillingForProvider: !sp.isAllowedToBill,
    clientAssignmentStatus: sp.status as any,
    districtsOfOperation: districts.map((d) => ({ id: d.id, name: d.name })),
    assignedSchoolCampuses: shimmedCampuses,
    activeSchoolCampuses: shimmedCampuses,
    medicaidCredentials: credentials as any,
    studentCaseload: shimmedStudents,
    serviceProviderTypes: shimmedProviderTypes,
  };
}
